import { FC, useState } from 'react'
import { Link } from 'react-router-dom'
import cn from 'classnames'
import accounting from 'accounting'

import { useCheckMobile } from '@/hooks/useCheckMobile'

import { IAction, IMessage, IMessageItem, StringObject } from '@/context/types'
import ChatBubble from '@/components/ChatBubble'
import Modal from '@/components/Modal'
import WorkOrderModalContent from '../CarouselType/components/WorkOrderModalContent'

import useMessenger from '@/hooks/useMessenger'
import useDebounce from '@/hooks/useDebounce'

import { CONTENT_TYPES, DIRECTION, MESSAGE_TYPES } from '@/context/constants'
import {
  WORK_ORDER_PRIORITY_STYLE,
  WORK_ORDER_STATUS,
  WORK_ORDER_STATUS_STYLE,
} from '../constants'

interface WorkOrderCarouselProps {
  message: IMessage
}

const WorkOrderCarousel: FC<WorkOrderCarouselProps> = ({ message }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const { isConversationNew, getSelectedConversation, getConfig, sendMessage } =
    useMessenger()
  const isMobile = useCheckMobile()

  const openModal = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const handleOnClick = (actions: IAction[], metadata: StringObject) => {
    if (actions.length === 0) {
      return
    }

    const { text, payload } = actions[0]

    const sendPayload = {
      type: MESSAGE_TYPES.MESSAGE,
      conversationId: getSelectedConversation(),
      author: {
        type: getConfig()?.userType,
        userExternalId: getConfig()?.userExternalId,
      },
      content: {
        type: CONTENT_TYPES.WORK_ORDER_CARD,
        text,
        payload,
      },
      metadata: {
        source: 'poplar-messenger',
        ...metadata,
      },
    }

    sendMessage(sendPayload)
  }

  return (
    <>
      {message.content?.items?.length > 0 && message.content.text && (
        <div>
          <div className="sm:pmui-text-xs md:pmui-text-sm pmui-my-3 pmui-ml-2">
            {message.content.text}
          </div>
          <div className="carousel-container pmui-py-2 pmui-px-2">
            {message.content.items.map((item: IMessageItem) => {
              const {
                workOrderNumber,
                workStatus,
                priority,
                grandTotal,
                propertyID,
                workOrderID,
              } = item?.metadata || {}
              return (
                <button
                  key={workOrderNumber}
                  className={cn(
                    'pmui-mr-4 pmui-p-3 pmui-w-72 pmui-h-52 pmui-flex pmui-flex-col pmui-flex-shrink-0 pmui-text-sm pmui-text-left pmui-rounded-lg carousel-box',
                    'pmui-w-full pmui-max-w-xs pmui-h-full',
                    {
                      'pmui-cursor-pointer hover:pmui-bg-gray-100':
                        isConversationNew,
                    },
                  )}
                  disabled={!isConversationNew}
                  onClick={useDebounce(() => {
                    const actions = [
                      {
                        type: 'postback',
                        text: workOrderNumber,
                        payload: '',
                        uri: '',
                      },
                    ]
                    handleOnClick(actions, {
                      ...item?.metadata,
                      title: item.title,
                    })
                    closeModal()
                  }, getConfig()?.debounceTimeout)}
                >
                  <div className="pmui-flex pmui-justify-between pmui-items-center pmui-w-full">
                    <div>{workOrderNumber}</div>
                    <div
                      className={cn(
                        `pmui-px-2 pmui-py-2 pmui-rounded pmui-text-xs`,
                        WORK_ORDER_STATUS_STYLE[
                          WORK_ORDER_STATUS[workStatus || '']
                        ],
                      )}
                    >
                      {WORK_ORDER_STATUS[workStatus || '']}
                    </div>
                  </div>
                  <div className="pmui-w-full pmui-text-base pmui-font-semibold pmui-mt-1 pmui-flex-1">
                    {item.title}
                  </div>
                  <div className="pmui-text-sm pmui-mb-2">
                    {`${item.metadata?.street}${
                      item.metadata?.unitNumber
                        ? `, Unit ${item.metadata?.unitNumber}`
                        : ''
                    }`}
                  </div>
                  <div className="pmui-flex pmui-justify-between pmui-items-center pmui-w-full">
                    <div className="pmui-flex pmui-items-center">
                      <div
                        className={cn(
                          'dot pmui-mr-2',
                          WORK_ORDER_PRIORITY_STYLE[priority || 'Common'],
                        )}
                      />
                      <div className={'pmui-text-sm pmui-text-pop-gray-300'}>
                        {priority || 'Common'}
                      </div>
                    </div>
                    <div className={'pmui-text-sm'}>
                      Total Est: {accounting.formatMoney(grandTotal || 0, '$')}
                    </div>
                  </div>
                  <div className="horizontal-divider pmui-my-2 pmui-w-full" />
                  <div className="pmui-text-center pmui-w-full">
                    <Link
                      to={{
                        pathname: `/owner/maintenance/${propertyID}/wo/${workOrderID}`,
                        state: {
                          chatWidgetOpen: isMobile ? false : true,
                        },
                      }}
                      onClick={e => {
                        e.stopPropagation()
                      }}
                    >
                      <div
                        className="sm:pmui-text-base xs:pmui-text-sm pmui-font-eina03 pmui-font-semibold \
                              pmui-text-pop-orange pmui-whitespace-pre-wrap hover:pmui-cursor-pointer hover:pmui-underline"
                      >
                        VIEW
                      </div>
                    </Link>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={true}
      >
        <WorkOrderModalContent
          closeModal={closeModal}
          handleOnClick={handleOnClick}
        />
      </Modal>
      <ChatBubble direction={DIRECTION.LEFT}>
        <div className="sm:pmui-text-sm xs:pmui-text-xs">
          <div>
            {message.content?.items?.length > 0
              ? 'Cannot find your work order?'
              : 'You currently have no open work orders.'}
          </div>
          <button
            className="pmui-mt-2 sm:pmui-text-sm xs:pmui-text-xs pmui-text-pop-orange pmui-underline pmui-font-semibold pmui-cursor-pointer"
            onClick={() => openModal()}
            disabled={!isConversationNew}
          >
            View all Work Orders
          </button>
        </div>
      </ChatBubble>
    </>
  )
}

export default WorkOrderCarousel
